import {LogEntryItem, SearchLogEntriesParams, SearchLogEntriesResponse} from "@app/_models/api";
import {EdiFileSummaryBaseComponent} from "@app/processing/edi-file-summary-base/edi-file-summary-base.component";
import {parseUtc} from "@app/processing/request-history/request-history.helpers";
import {SortDirection} from "@app/_directives/sortable.directive";

export const DEFAULT_WINDOW_DAYS = 7;
export const VINEMEDS_ADMIN_CUSTOMER_ID = "00000000-0000-0000-0000-000000000000";

const IDENTIFIER_TABLES = ["DISTRIBUTION_CENTER_IDENTIFICATION_CODE", "PRODUCT_IDENTIFIER"];
const LOCALE = "en-US";
const EASTERN_TIME = "America/New_York";

export type AuditLogFilters = {
    customerId: string;
    cognitoUserId: string;
    tableName: string;
    from: string;
    to: string;
    value: string;
    contains: boolean;
};

// The API searches whole US Eastern days, so dates and times on this page use US Eastern time, not the browser's.
function easternParts(date: Date, hour12: boolean): Record<string, string> {
    const parts = new Intl.DateTimeFormat(LOCALE, {
        timeZone: EASTERN_TIME,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        ...(hour12 ? {hour12: true} : {hourCycle: "h23" as const})
    }).formatToParts(date);
    return Object.fromEntries(parts.map(part => [part.type, part.value]));
}

export function easternDate(date: Date): string {
    const {year, month, day} = easternParts(date, false);
    return `${year}-${month}-${day}`;
}

export function formatEasternDateTime(value: string): string {
    if (!value) {
        return "";
    }

    const {year, month, day, hour, minute, dayPeriod} = easternParts(parseUtc(value), true);
    return `${year}-${month}-${day}, ${hour}:${minute} ${dayPeriod}`;
}

export function defaultFilters(now: Date = new Date()): AuditLogFilters {
    const to = easternDate(now);
    const [year, month, day] = to.split("-").map(Number);
    // Calendar arithmetic on the Eastern date, so a daylight-saving change cannot shift the window by a day.
    const from = new Date(Date.UTC(year, month - 1, day - (DEFAULT_WINDOW_DAYS - 1))).toISOString().slice(0, 10);

    return {
        customerId: "",
        cognitoUserId: "",
        tableName: "",
        from,
        to,
        value: "",
        contains: false
    };
}

export function toSearchParams(filters: AuditLogFilters): SearchLogEntriesParams {
    const trimmed = filters.value.trim();
    // An exact search keeps surrounding spaces, so a stored value that has them can still be found.
    const value = trimmed && !filters.contains ? filters.value : trimmed;

    return {
        customerId: filters.customerId,
        cognitoUserId: filters.cognitoUserId,
        tableName: filters.tableName,
        from: filters.from,
        to: filters.to,
        value,
        valueMatch: value && filters.contains ? "contains" : ""
    };
}

export function recordText(item: LogEntryItem): string {
    const record = item.recordName ?? item.primaryKey;
    return IDENTIFIER_TABLES.includes(item.tableName)
        ? `code ${record}, ${item.ownerName ?? "owner unknown"}`
        : record;
}

export function totalText(shown: number, total: number): string {
    return shown < total
        ? `${shown.toLocaleString(LOCALE)} of ${total.toLocaleString(LOCALE)} Total`
        : `${total.toLocaleString(LOCALE)} Total`;
}

const SORT_VALUES: Record<AuditLogSortColumn, (item: LogEntryItem) => string> = {
    createdAt: item => item.createdAt,
    userName: item => item.userName,
    customerName: item => item.customerName ?? "",
    tableName: item => item.tableName,
    record: recordText,
    columnName: item => item.columnName,
    oldValue: item => item.oldValue ?? "",
    newValue: item => item.newValue ?? ""
};

export type AuditLogSortColumn =
    "createdAt" | "userName" | "customerName" | "tableName" | "record" | "columnName" | "oldValue" | "newValue";

export function sortItems(items: LogEntryItem[], column: AuditLogSortColumn | "", direction: SortDirection): LogEntryItem[] {
    if (!column || !direction) {
        return items;
    }

    const value = SORT_VALUES[column];
    const sign = direction === "asc" ? 1 : -1;

    // Empty values go last in both directions.
    return [...items].sort((a, b) => {
        const left = value(a);
        const right = value(b);
        if (!left || !right) {
            return Number(!left) - Number(!right);
        }

        return sign * left.localeCompare(right, LOCALE, {numeric: true, sensitivity: "base"});
    });
}

export function truncationNote(response: SearchLogEntriesResponse): string | null {
    if (response.totalCount <= response.limit) {
        return null;
    }

    const shown = response.items.length.toLocaleString(LOCALE);
    const total = response.totalCount.toLocaleString(LOCALE);
    return `Showing the newest ${shown} of ${total} changes. Narrow the filters to see the rest.`;
}

export function buildCsv(items: LogEntryItem[]): string {
    const header = ["When (US Eastern)", "Who", "Who Email", "Customer", "Customer Code", "Table", "Record", "Column",
        "Old", "New", "Primary Key", "Correlation ID"];
    const rows = items.map(item => {
        const {year, month, day, hour, minute, second} = easternParts(parseUtc(item.createdAt), false);
        return [
            `${year}-${month}-${day} ${hour}:${minute}:${second}`,
            item.userName,
            item.userEmail ?? "",
            item.customerName ?? "",
            item.customerCode ?? "",
            item.tableName,
            recordText(item),
            item.columnName,
            item.oldValue ?? "",
            item.newValue ?? "",
            item.primaryKey,
            item.correlationId
        ];
    });

    return [header, ...rows]
        .map(row => row.map(EdiFileSummaryBaseComponent.toCsvField).join(","))
        .join("\r\n");
}
